// 举手报告的右侧常驻卡：组长 / 值班长在**任意页面**都能一眼看到最新报告并直接介入。
// 演示版：动作只改本地 mock 状态并提示，不真的监听/强插/查内容；数据与收件箱同源。
//
// 展示优先级：待响应 → 处理中，同状态按等待时长从长到短；最多铺 2 条，其余给「还有 N 条」。
// 位置固定在页面右侧（见 styles/components/support-request.css 的 .bcc-support-rail），可收起。

import { useEffect, useMemo, useState } from 'react';
import { Button, Message, Popconfirm, Tag } from '@arco-design/web-react';
import { IconDown, IconExclamationCircle, IconUp } from '@arco-design/web-react/icon';
import { useViewRole } from '../../hooks/useViewRole';
import { recordOpsLog } from '../operations/opsLog';
import { supportForReceiver, useSupportMock, type MockSupportItem } from './mock';

/** 右侧卡展示全部待跟进报告：高度上限半屏，超出在卡内滚动；完整清单仍可去收件箱 */

const ROLE_LABEL: Record<'leader' | 'duty', string> = {
  leader: '组长（示例）',
  duty: '值班长（示例）',
};

function fmtWaited(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function ReportCard({
  item,
  by,
  onAck,
  onResolve,
  onLog,
}: {
  item: MockSupportItem;
  by: string;
  onAck: (id: string) => void;
  onResolve: (id: string) => void;
  onLog: (action: string) => void;
}) {
  const accepted = item.status === 'accepted';
  return (
    <div className="bcc-support-rail-item">
      <div className="bcc-support-rail-item-head">
        <Tag size="small" color="arcoblue">
          {item.typeLabel}
        </Tag>
        <b className="bcc-support-rail-from">{item.fromName}</b>
        {item.fromExtra ? <span className="bcc-support-rail-dim">{item.fromExtra}</span> : null}
        <span className="bcc-support-rail-waited">已等 {fmtWaited(item.waitingSec)}</span>
      </div>

      <div className="bcc-support-rail-subject">{item.subject}</div>

      {item.context && item.context.length > 0 ? (
        <div className="bcc-support-rail-context">
          {item.context.slice(0, 2).map((c) => (
            <div key={c} className="bcc-support-rail-context-line">
              {c}
            </div>
          ))}
        </div>
      ) : null}

      <div className="bcc-support-rail-status">
        {accepted ? `处理中 · ${item.assignee || by}` : '待响应'}
        {item.customerWaiting ? <span className="bcc-support-rail-wait-tag">客户在等</span> : null}
      </div>

      <div className="bcc-support-rail-actions">
        <Button
          size="mini"
          type="primary"
          disabled={accepted}
          onClick={() => {
            onAck(item.id);
            onLog('接听举手报告');
          }}
        >
          接听
        </Button>
        <Button
          size="mini"
          onClick={() => {
            onAck(item.id);
            onLog('强插');
            Message.info('演示：真实环境会以三方方式插入该坐席的通话');
          }}
        >
          强插
        </Button>
        <Popconfirm
          title="强拆这通电话？客户与坐席会被立即断开，通话记录保留且不可恢复。"
          okText="强拆"
          cancelText="取消"
          okButtonProps={{ status: 'danger' }}
          onOk={() => {
            onLog('强拆');
            Message.info('演示：真实环境会强制挂断该坐席当前通话');
          }}
        >
          <Button size="mini" status="danger">
            强拆
          </Button>
        </Popconfirm>
        <Button size="mini" type="text" onClick={() => onResolve(item.id)}>
          标记已处理
        </Button>
      </div>
    </div>
  );
}

/**
 * 右侧常驻报告卡。只对组长 / 值班长渲染；没有待跟进报告时整卡不出现，不占屏幕。
 * 数据来自 `useSupportMock`（与收件箱同源），真实接入后这一层换成轮询 / WS 即可。
 */
export default function SupportAlertRail() {
  const viewRole = useViewRole();
  const items = useSupportMock((s) => s.items);
  const ack = useSupportMock((s) => s.ack);
  const resolve = useSupportMock((s) => s.resolve);
  const [collapsed, setCollapsed] = useState(false);
  /** 页面停留期间继续累加等待秒数：报告是「此刻还在等」，不能停在挂载那一刻 */
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = window.setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const role = viewRole === 'leader' || viewRole === 'duty' ? viewRole : null;

  const all = useMemo(() => {
    if (!role) return [];
    return supportForReceiver(items, role)
      .filter((i) => i.status !== 'resolved')
      .map((i) => ({ ...i, waitingSec: i.waitingSec + elapsed }))
      .sort((a, b) => {
        // 待响应优先，其次处理中；同状态等得久的排前面
        const rank = (s: string) => (s === 'pending' ? 0 : 1);
        return rank(a.status) - rank(b.status) || b.waitingSec - a.waitingSec;
      });
  }, [items, role, elapsed]);

  if (!role || all.length === 0) return null;

  const by = ROLE_LABEL[role];

  return (
    <div className={`bcc-support-rail${collapsed ? ' bcc-support-rail-collapsed' : ''}`} role="complementary" aria-label="举手报告">
      {/* 整个卡头都是展开/收起的开关（原来只有右侧那颗小图标能点，鼠标要瞄准）：
          卡头做成一个可聚焦的按钮，右侧箭头只是它的视觉指示，不再单独可点——避免「按钮套按钮」
          与「点箭头冒泡到卡头」这类双触发。 */}
      <div
        className="bcc-support-rail-head"
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-label={collapsed ? '展开举手报告' : '收起举手报告'}
        onClick={() => setCollapsed((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((v) => !v);
          }
        }}
      >
        <IconExclamationCircle className="bcc-support-rail-icon" />
        <span className="bcc-support-rail-title">举手报告</span>
        <span className="bcc-support-rail-count">{all.length} 条待跟进</span>
        {collapsed ? <IconDown className="bcc-support-rail-toggle" /> : <IconUp className="bcc-support-rail-toggle" />}
      </div>

      {collapsed ? null : (
        <div className="bcc-support-rail-body">
          {all.map((item) => (
            <ReportCard
              key={item.id}
              item={item}
              by={by}
              onAck={(id) => {
                ack(id, '接听中');
                Message.success(`已接听（演示）：${by}`);
              }}
              onResolve={(id) => {
                resolve(id, '已处理');
                recordOpsLog({
                  scope: role,
                  actor: by,
                  action: '举手报告已处理',
                  detail: `${item.fromName} · ${item.subject}`,
                });
                Message.success('已标记处理（演示）');
              }}
              onLog={(action) => {
                recordOpsLog({
                  scope: role,
                  actor: by,
                  action,
                  detail: `${item.fromName} · ${item.subject}`,
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
